'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'

import type { HolidayItinerarySection } from '@/src/types/holidays'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'

import ItineraryTimeline, { type ItineraryDayView } from './itineraryTimeline'

interface ItineraryTimelineWrapperProps {
    section: HolidayItinerarySection
    isMobile: boolean
}

const ItineraryTimelineWrapper: React.FC<ItineraryTimelineWrapperProps> = ({ section, isMobile }) => {
    const t = useTranslations('holidaysPage')
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()

    // Day 1 is open on load; the rest are collapsed so a nine-day itinerary is still scannable.
    // A set, not one index, because "Expand all" opens every day at once.
    const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set([0]))

    // Only an opening day is an engagement; collapsing one is not.
    const trackDayOpened = useCallback(
        (index: number, engagementType: string) => {
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: 'itinerary',
                engagement_type: engagementType,
                day_index: index + 1
            })
        },
        [triggerHolidayUIActionEvent]
    )

    const handleToggleDay = useCallback(
        (index: number) => {
            setExpanded(current => {
                const next = new Set(current)
                if (next.has(index)) next.delete(index)
                else {
                    next.add(index)
                    trackDayOpened(index, 'expand')
                }
                return next
            })
        },
        [trackDayOpened]
    )

    const days: ItineraryDayView[] = useMemo(
        () =>
            (section.days ?? []).map((day, index) => ({
                dayLabel: day.dayLabel,
                title: day.title,
                highlights: day.highlights,
                images: day.images,
                // "Day 1" / "اليوم 1" — the numeral is what the chip shows, whatever the word.
                dayNumber: day.dayLabel.match(/\d+/)?.[0] ?? String(index + 1),
                experienceCount: day.highlights?.length ?? 0
            })),
        [section.days]
    )

    const allExpanded = days.length > 0 && expanded.size === days.length

    const handleToggleAll = useCallback(() => {
        setExpanded(current => {
            if (current.size === days.length) return new Set<number>()
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: 'itinerary',
                engagement_type: 'expand_all',
                day_index: days.length
            })
            return new Set(days.map((_day, index) => index))
        })
    }, [days, triggerHolidayUIActionEvent])

    if (!days.length) return null

    return (
        <ItineraryTimeline
            days={days}
            expanded={expanded}
            onToggleDay={handleToggleDay}
            title={t('itineraryTitle')}
            subtitle={t('itinerarySubtitle')}
            toggleAllLabel={allExpanded ? t('itineraryCollapseAll') : t('itineraryExpandAll')}
            onToggleAll={handleToggleAll}
            experienceCountLabel={count => t('itineraryExperiences', { count })}
            photosLabel={count => t('itineraryPhotos', { count })}
            runningOrderLabel={t('itineraryHowTheDayGoes')}
            stepsShowMoreLabel={count => t('listShowMore', { count })}
            stepsShowLessLabel={t('listShowLess')}
            isMobile={isMobile}
        />
    )
}

export { ItineraryTimelineWrapper }
export default ItineraryTimelineWrapper
